import React from 'react';
import { Navigate } from 'react-router-dom';
// Deep import (not the @features/organizations barrel) to keep the route
// module's graph lean and avoid the org page-component cycle.
import { usePracticePlanDestination } from '@features/organizations/store/hooks';
import { useCan, useOrganizationInfo } from '@features/user';

/**
 * Index redirect for `/practice` (and the legacy `/group_administration`
 * alias). Group orgs land on the Members page; solo orgs land on the
 * Subscription page — solo orgs don't have a Members surface, so sending them
 * to `/practice/clinicians` would just bounce through CliniciansPage's own
 * solo guard. This picks the right destination in one hop.
 */
export const PracticeIndexRedirect: React.FC = () => {
  const organizationInfo = useOrganizationInfo();
  const { route } = usePracticePlanDestination();
  const canManageSubscription = useCan('manage_subscription');
  const canManageAppointmentTypes = useCan('manage_appointment_types');

  // Hold until UserInfo resolves. `usePracticePlanDestination` defaults to
  // `isGroup = false` while `organization_info` is undefined, so redirecting
  // eagerly snaps a group-org user to /practice/subscriptions on every hard
  // load of /practice or /group_administration (the latter mounts this outside
  // PracticeLayout, so there's no upstream gate). Mirrors the
  // `organizationInfo != null` guard in CliniciansPage.
  if (!organizationInfo) return null;

  if (!canManageSubscription && canManageAppointmentTypes) {
    // GDN-2482: appointment types now live in the consolidated
    // Schedule & Documentation tab.
    return <Navigate to="/practice/schedule-documentation" replace />;
  }

  return <Navigate to={route} replace />;
};
